"use client";

import React, { useEffect, useRef, useState } from "react";
import { ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  title: "Submersible Internal Rig",
  description: "Technical schematics and physical construction of the Poseidon-class pressure hull used for interior bridge sequences.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/scene-3-shot-1.png?v=bv7zbyiwop4",
  spanClasses: "md:col-span-2 md:row-span-2"
}, {
  id: 2,
  title: "Tactical HUD Calibration",
  description: "Custom-built user interface designs for the command deck displays, featuring real-time sonar data simulation.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/poseidong_project_scene_4_sc4_shot2.png?v=ppazhg986df",
  spanClasses: "md:col-span-1 md:row-span-1"
}, {
  id: 3,
  title: "Abyssal Pressure Hull",
  description: "Detailed texture study of the external titanium plating, weathered to simulate thousands of hours in high-pressure depths.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/scene-3-shot-3.png?v=osez82b7io",
  spanClasses: "md:col-span-1 md:row-span-2"
}, {
  id: 4,
  title: "Cinematic Lighting Core",
  description: "Bioluminescent cyan lighting arrays designed to create the claustrophobic atmosphere of a submarine in stealth mode.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/poseidon_project_scene_5_sc5_shot5.2.png?v=ojjb8u6qi8s",
  spanClasses: "md:col-span-1 md:row-span-1"
}, {
  id: 5,
  title: "VFX Depth Simulation",
  description: "Behind-the-scenes look at the digital lighting passes used to recreate the murky, light-starved environment of the deep trench.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/SCENE-9-STORYBOARD-scaled.jpg?v=rgpou6bac2",
  spanClasses: "md:col-span-2 md:row-span-1"
}, {
  id: 6,
  title: "Naval Prop Archive",
  description: "A collection of custom-fabricated equipment and tactical gear used by the crew during the high-stakes thriller sequences.",
  src: "https://theposeidonproject.com/wp-content/uploads/2026/05/poseidong_project_scene_4_sc4_shot3.png?v=lulkanvhmk9",
  spanClasses: "md:col-span-2 md:row-span-1"
}];
export default function EngineeringExcellence() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="145976" id="behind-the-scenes-intel" className="dark bg-background py-24 md:py-32 overflow-hidden border-t border-primary/10">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-foreground mb-6 tracking-widest uppercase">
            Classified Production Intel
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-mono max-w-2xl border-l-2 border-primary/50 pl-6">
            Accessing encrypted visual logs from the production of The Poseidon Project. These assets document the technical engineering and cinematic craft required to bring the deep-sea thriller to life.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-4 auto-rows-[300px] md:auto-rows-[320px] gap-4 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {galleryItems.map((item, i) => <Dialog key={item.id}>
              <DialogTrigger asChild data-index={i}>
                <button className={`group relative overflow-hidden bg-card w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background border border-primary/10 transition-all duration-500 hover:border-primary/40 ${item.spanClasses}`} aria-label={`View high resolution intelligence for ${item.title}`}>
                  <img src={item.src} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110 grayscale-[0.5] group-hover:grayscale-0" loading={i > 2 ? "lazy" : "eager"} />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 backdrop-blur-[1px] group-hover:backdrop-blur-0 transition-all duration-500 flex items-center justify-center">
                    <div className="bg-background/80 backdrop-blur-md text-primary p-3 rounded-none border border-primary/50 opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-90 group-hover:scale-100 shadow-[0_0_15px_rgba(0,255,255,0.3)]">
                      <ZoomIn className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-primary font-sans text-sm font-bold tracking-[0.2em] uppercase drop-shadow-md">
                      {item.title}
                    </span>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl p-2 md:p-4 bg-card border-primary/30 shadow-[0_0_50px_rgba(0,0,0,0.8)] rounded-none">
                <DialogHeader className="sr-only">
                  <DialogTitle>{item.title}</DialogTitle>
                  <DialogDescription>{item.description}</DialogDescription>
                </DialogHeader>
                <div className="relative w-full h-full flex flex-col gap-4">
                  <div className="relative w-full overflow-hidden bg-muted border border-primary/10 rounded-none">
                    <img src={item.src} alt={item.title} className="w-full h-auto max-h-[75vh] object-contain" />
                    <div className="absolute top-4 left-4 bg-primary/10 backdrop-blur-md border border-primary/30 px-3 py-1">
                      <p className="text-[10px] font-mono text-primary tracking-widest uppercase">Encrypted Asset</p>
                    </div>
                  </div>
                  <div className="px-4 pb-4 border-l-2 border-primary/40">
                    <h3 className="text-2xl font-sans font-bold text-foreground mb-2 tracking-wider uppercase">{item.title}</h3>
                    <p className="text-muted-foreground font-mono leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>)}
        </div>
      </div>
    </section>;
}